import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Box,
  FileSpreadsheet,
  Flag,
  History,
  LogOut,
  Megaphone,
  MessageSquare,
  PlaySquare,
  Receipt,
  Sparkles,
  Tag,
  Users,
  Webhook,
} from 'lucide-react';

import { auth } from '@/api/admin';
import { useAuthStore } from '@/auth/store';

const navItems = [
  { to: '/', label: 'Overview', icon: BarChart3, end: true },
  { to: '/users', label: 'Users', icon: Users },
  { to: '/jobs', label: 'Jobs', icon: PlaySquare },
  // v3 — Razorpay-driven billing nav.
  { to: '/billing/plans', label: 'Plans', icon: Tag },
  { to: '/billing/subscriptions', label: 'Subscriptions', icon: Receipt },
  { to: '/billing/payment-events', label: 'Webhook log', icon: Webhook },
  // v3 — AI policy compliance.
  { to: '/ai-flags', label: 'AI flags', icon: Flag },
  // v3 — tax exports (GST / TDS / FIRC monthly summary).
  { to: '/exports', label: 'Tax exports', icon: FileSpreadsheet },
  { to: '/broadcast', label: 'Broadcast', icon: Megaphone },
  { to: '/broadcast/history', label: 'Broadcast log', icon: History },
  { to: '/ai-usage', label: 'AI usage', icon: Sparkles },
  { to: '/runtimes', label: 'Runtimes', icon: Box },
  { to: '/feedback', label: 'Feedback', icon: MessageSquare },
];

export function BrandMark({ size = 32 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 240 240"
      fill="none"
      aria-hidden="true"
    >
      <g
        transform="translate(120,120)"
        strokeLinejoin="round"
        strokeLinecap="round"
      >
        <polygon
          points="0,-95 82,-47 82,47 0,95 -82,47 -82,-47"
          stroke="#F5EFE3"
          strokeWidth="10"
        />
        <path d="M -36 -28 L -58 0 L -36 28" stroke="#F5EFE3" strokeWidth="10" />
        <path d="M  36 -28 L  58 0 L  36 28" stroke="#F5EFE3" strokeWidth="10" />
        <line
          x1="-19"
          y1="36"
          x2="19"
          y2="-36"
          stroke="#C2683A"
          strokeWidth="10"
        />
      </g>
    </svg>
  );
}

export default function Layout() {
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();

  async function signOut() {
    try {
      await auth.logout();
    } catch {
      // local clear is what matters even if the server call fails
    } finally {
      clear();
      navigate('/login', { replace: true });
    }
  }

  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-10 flex h-14 items-center justify-between border-b border-border bg-bg px-4 md:px-6">
        <div className="flex items-center gap-3">
          <BrandMark size={28} />
          <div className="leading-tight">
            <div className="text-sm font-semibold tracking-tight">
              OffCoder Admin
            </div>
            <div className="text-[11px] text-text-tertiary">{user?.email}</div>
          </div>
        </div>
        <button
          onClick={signOut}
          className="btn-ghost text-xs"
          aria-label="Sign out"
        >
          <LogOut className="h-4 w-4" />
          <span className="hidden sm:inline">Sign out</span>
        </button>
      </header>

      <div className="flex flex-1">
        <aside className="hidden w-56 flex-shrink-0 border-r border-border bg-bg-low/40 p-3 md:block">
          <nav className="flex flex-col gap-1">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors duration-100 ${
                    isActive
                      ? 'bg-brand-primary/15 text-brand-accent'
                      : 'text-text-secondary hover:bg-bg-high hover:text-text-primary'
                  }`
                }
              >
                <it.icon className="h-4 w-4" />
                {it.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="flex-1 px-4 py-6 md:px-8 md:py-8">
          <Outlet />
        </main>
      </div>

      {/* Bottom nav for mobile — solid bg, no blur (avoids per-frame repaint) */}
      <nav className="sticky bottom-0 z-10 flex border-t border-border bg-bg-surface md:hidden">
        {navItems.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center gap-0.5 px-2 py-2 text-[10px] ${
                isActive ? 'text-brand-accent' : 'text-text-tertiary'
              }`
            }
          >
            <it.icon className="h-4 w-4" />
            {it.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
